import { useMemo } from 'react';

import {
  Configuration,
  RestMultiProcessOffersApiFactory,
  RestOfferAiChatApiFactory,
  RestOfferPrintApiFactory,
  RestOfferPrintOwnerPageApiFactory,
  RestProcessActivityApiFactory,
  RestProcessesApiFactory,
  RestProcessesCustomerApiFactory,
  RestProcessesEventBasicApiFactory,
  RestProcessesEventCalendarApiFactory,
  RestProcessOfferElementsApiFactory,
  RestProcessOfferEmailAttachmentApiFactory,
  RestProcessOfferFileGeneratorApiFactory,
  RestProcessOffersApiFactory,
  RestResourcesNoteApiFactory,
  RestResourcesThreadApiFactory,
  RestResourcesTimelineMessagesApiFactory,
} from '@/api/generated';
import { CONFIG } from '@/config/config.ts';

import { useAxiosInstance } from '../useAxiosInstance.util.tsx';

interface Return {
  offerAiChatApi: ReturnType<typeof RestOfferAiChatApiFactory>;
  offerPrintApi: ReturnType<typeof RestOfferPrintApiFactory>;
  offerPrintOwnerPageApi: ReturnType<typeof RestOfferPrintOwnerPageApiFactory>;
  processActivityApi: ReturnType<typeof RestProcessActivityApiFactory>;
  processesApi: ReturnType<typeof RestProcessesApiFactory>;
  processesCustomerApi: ReturnType<typeof RestProcessesCustomerApiFactory>;
  processesEventBasicApi: ReturnType<typeof RestProcessesEventBasicApiFactory>;
  processesEventCalendarApi: ReturnType<typeof RestProcessesEventCalendarApiFactory>;
  processesOffersApi: ReturnType<typeof RestProcessOffersApiFactory>;
  processMultiOffersApi: ReturnType<typeof RestMultiProcessOffersApiFactory>;
  processOfferElementsApi: ReturnType<typeof RestProcessOfferElementsApiFactory>;
  processOfferEmailAttachmentApi: ReturnType<typeof RestProcessOfferEmailAttachmentApiFactory>;
  processOfferFileGenerator: ReturnType<typeof RestProcessOfferFileGeneratorApiFactory>;
  resourceNoteApi: ReturnType<typeof RestResourcesNoteApiFactory>;
  resourceThreadApi: ReturnType<typeof RestResourcesThreadApiFactory>;
  resourceTimelineMessagesApi: ReturnType<typeof RestResourcesTimelineMessagesApiFactory>;
}

export const useProcessApiClient = (): Return => {
  const basePath = CONFIG.HOST;
  const configuration = getConfiguration(basePath);
  const axiosInstance = useAxiosInstance();

  return useMemo(() => {
    return {
      offerAiChatApi: RestOfferAiChatApiFactory(configuration, undefined, axiosInstance),
      offerPrintApi: RestOfferPrintApiFactory(configuration, undefined, axiosInstance),
      offerPrintOwnerPageApi: RestOfferPrintOwnerPageApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      processActivityApi: RestProcessActivityApiFactory(configuration, undefined, axiosInstance),
      processesApi: RestProcessesApiFactory(configuration, undefined, axiosInstance),
      processesCustomerApi: RestProcessesCustomerApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      processesEventBasicApi: RestProcessesEventBasicApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      processesEventCalendarApi: RestProcessesEventCalendarApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      processesOffersApi: RestProcessOffersApiFactory(configuration, undefined, axiosInstance),
      processMultiOffersApi: RestMultiProcessOffersApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      processOfferElementsApi: RestProcessOfferElementsApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      processOfferEmailAttachmentApi: RestProcessOfferEmailAttachmentApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      processOfferFileGenerator: RestProcessOfferFileGeneratorApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      resourceNoteApi: RestResourcesNoteApiFactory(configuration, undefined, axiosInstance),
      resourceThreadApi: RestResourcesThreadApiFactory(configuration, undefined, axiosInstance),
      resourceTimelineMessagesApi: RestResourcesTimelineMessagesApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
    };
  }, [axiosInstance]);
};

const getConfiguration = (basePath: string): Configuration => ({
  basePath,
  isJsonMime(_mime: string): boolean {
    return true;
  },
});
